import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useConsumer } from "../ConsumerProvider";
import { useFaceVerification, useIProovEvents, useBiometricConsent } from "../../hooks/faceVerification";
import type { ConsentFailure } from "../../hooks/faceVerification/useIProovEvents";
import { getConsentApiBaseUrl } from "../../shared/utils/apiConfig";
import { trackAPIError } from "../../shared/utils/sentryTracking";
import { getIProovLanguage } from "../../locales/iproov";
import { loadIProovWebSdk } from "../../shared/api/vendors/iproov";
import { initAnalytics, setAnalyticsLocale, reportAnalyticsSetupFailure } from "../../shared/analytics/analyticsClient";
import { trackClicked } from "../../shared/analytics/events";
import { DOCUMENT_CAPTURE_EVENTS, DOCUMENT_CAPTURE_SECTIONS } from "../../shared/analytics/documentCaptureEvents";
import { IProovSlots } from "./slots";
import { Footer } from "./Footer";
import styles from "./FaceVerification.module.scss";
import { logger } from "../../shared/utils/logger";
import { preloadIProovAssets } from "../../shared/utils/iproovPreload";

/**
 * FaceVerification Component
 *
 * Handles the complete face verification flow using iProov Web SDK.
 * This component manages:
 * - Session initialization with backend
 * - iProov web component integration
 * - Event handling (ready, started, streaming, progress, passed, failed, etc.)
 * - Backend polling for verification results
 * - Communication with parent window via postMessage
 *
 * States:
 * - initializing: Setting up verification session
 * - ready: iProov is ready to start
 * - capturing: Face scanning in progress
 * - processing: Verifying results with backend
 * - completed: Verification successful
 * - error: Verification failed
 */
/** How long to wait for the host's retry decision before falling back to onError. */
const RETRY_DECISION_TIMEOUT_MS = 15000;

export function FaceVerification() {
  const { t, i18n } = useTranslation();
  const { fragnoLogoEnabled } = useConsumer();
  const iproovRef = useRef<HTMLElement>(null);
  const consentFailureRef = useRef<ConsentFailure | null>(null);

  // At most once per mount. The consent-failure path cancels eagerly (it wants the
  // transaction aborted immediately, not at the next state change), and the teardown
  // below would otherwise cancel the same transaction a second time. A repeat cancel
  // re-fires `error_transaction_canceled` after consentFailureRef has been consumed,
  // which handleError would then report as a generic failure instead of the consent one.
  const cancelledRef = useRef(false);

  const [showFlashingLightsLink, setShowFlashingLightsLink] = useState(true);
  const [isFlashingLightsOpen, setIsFlashingLightsOpen] = useState(false);
  const { definitionUuid, fetchConsent: fetchConsentDefinition } = useBiometricConsent();

  useEffect(() => {
    preloadIProovAssets();
  }, []);

  // Debug: Log current language
  useEffect(() => {
    logger.log('[FaceVerification] Component mounted with language:', i18n.language);
    logger.log('[FaceVerification] Sample translation test:', t('face.ready.heading'));
  }, [i18n.language, t]);

  // Use custom hooks for verification logic
  const {
    state,
    error,
    verificationId,
    sessionToken,
    iproovToken,
    iproovBaseUrl,
    progress,
    consentEnabled,
    setFlowStage,
    setProgress,
    initializeFaceVerification,
    pollVerificationStatus,
    pollVerificationStatusOnFailure,
    handleVerificationError,
    enterPermissionBlocked,
    enterCaptureFailed,
    retryCapture,
    retryAllowed,
    failOverToHost,
    requestHostReload,
    sendStepToParent,
    isSessionTokenExpiringSoon,
    blockedPermission,
  } = useFaceVerification();

  // Set up iProov event listeners
  useIProovEvents({
    iproovRef,
    iproovToken,
    verificationId,
    sessionToken,
    setFlowStage,
    setProgress,
    onVerificationPassed: pollVerificationStatus,
    onVerificationError: pollVerificationStatusOnFailure,
    onPermissionBlocked: enterPermissionBlocked,
    selfCancelledRef: cancelledRef,
    onCaptureFailed: enterCaptureFailed,
    consentFailureRef,
  });

  // If the host never answers VERIFICATION_ATTEMPT_FAILED, fall back to the previous
  // behaviour rather than stranding the member on a retry button that will never
  // enable. Longer than IVA's own poll budget (10 x 1s) so a slow-but-working host is
  // not cut off. A host that answers `false` is taking over, so no timer runs for it.
  useEffect(() => {
    if (state !== "capture_failed" || retryAllowed !== null) return;
    const timer = setTimeout(failOverToHost, RETRY_DECISION_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [state, retryAllowed, failOverToHost]);

  // Initialize on mount
  useEffect(() => {
    // Analytics setup must never block the core flow — if init throws, swallow it
    // so the parent handshake still runs (honors "a failure never blocks the flow").
    try {
      initAnalytics({ module: "face_liveness", provider: "iproov", locale: i18n.language });
    } catch (err) {
      logger.warn("[FaceVerification] analytics init failed; continuing without analytics", err);
      // Analytics: via the guard, not trackAnalyticsSetupError directly — a throw leaves the
      // client uninitialized, so the next emit() would report never_initialized for one cause.
      reportAnalyticsSetupFailure("init_failed", { flow: "face_liveness", error: err instanceof Error ? err.message : String(err) });
    }
    const cleanupFn = initializeFaceVerification();
    return cleanupFn;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Starts the download early; the session-init path awaits it and reports a failure.
  useEffect(() => {
    void loadIProovWebSdk().catch(() => {});
  }, []);

  // Analytics: the member can switch language mid-flow via the footer.
  useEffect(() => {
    setAnalyticsLocale(i18n.language);
  }, [i18n.language]);

  // Whether <iproov-me> is currently in the tree. Mirrors the render condition below;
  // keep the two in sync or the teardown stops firing.
  const iproovMounted =
    (state === "ready" || state === "capturing" || state === "permission_blocked" || state === "capture_failed") &&
    !!iproovToken &&
    !!iproovBaseUrl;

  // Cancel the in-flight transaction before <iproov-me> leaves the DOM.
  //
  // iProov require this: "This must be called before removing the `iproov-me` element
  // from the page." We only did it on the consent-failure path, so every other exit —
  // a member cancelling, any error, or the move to `processing` — tore the element out
  // with a live transaction still running.
  //
  // That matters because the element is remounted for a retry in the same JS context.
  // Observed on staging: cancel the first attempt, retry, and the second mount fails at
  // init with error_device_motion_unsupported on a desktop Mac — a device with no
  // gyroscope, where iProov's own support checker does not even register the gyro test
  // (it is gated on device.type of mobile/tablet). A device-capability code appearing
  // where no capability is being tested points at carried-over state, not the device.
  //
  // useLayoutEffect rather than useEffect so the cleanup runs in the commit phase
  // instead of after paint, which is the earliest React lets us intervene. The node is
  // captured in the closure so the call still lands if React has already detached it —
  // strictly better than the current behaviour either way.
  useLayoutEffect(() => {
    if (!iproovMounted) return;
    cancelledRef.current = false;
    const element = iproovRef.current;
    return () => {
      if (cancelledRef.current) return;
      cancelledRef.current = true;
      try {
        (element as unknown as { cancelTransaction?: () => void })?.cancelTransaction?.();
      } catch (err) {
        // Never let teardown throw — it runs during unmount and on the error path,
        // where an exception would mask the failure we are actually reporting.
        logger.warn("[FaceVerification] cancelTransaction failed during teardown:", err);
      }
    };
  }, [iproovMounted, iproovToken]);

  // Show link when .heading-verification is visible (iProov ready slot), hide on any other slot
  useEffect(() => {
    if (!iproovToken) return;
    const container = iproovRef.current;
    if (!container) return;

    let observer: ResizeObserver | undefined;

    const rafId = requestAnimationFrame(() => {
      const heading = container.querySelector<HTMLElement>('.heading-verification');
      if (!heading) return;

      observer = new ResizeObserver((entries) => {
        const height = entries[0]?.contentRect.height ?? 0;
        setShowFlashingLightsLink(height > 0);
      });
      observer.observe(heading);
    });

    return () => {
      cancelAnimationFrame(rafId);
      observer?.disconnect();
    };
  }, [iproovToken]);

  // Close flashing lights modal on Escape
  useEffect(() => {
    if (!isFlashingLightsOpen) return;
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') setIsFlashingLightsOpen(false); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isFlashingLightsOpen]);

  // Notify parent window on each trackable step transition
  useEffect(() => {
    const trackableStates = ["ready", "capturing", "processing"] as const;
    if (!trackableStates.includes(state as typeof trackableStates[number])) return;
    sendStepToParent(state as typeof trackableStates[number]);
  }, [state, sendStepToParent]);

  // Fetch consent definition UUID when sessionToken is available (only if consent is enabled)
  useEffect(() => {
    const fetchDefinition = async () => {
      if (!sessionToken || !consentEnabled) return;

      if (isSessionTokenExpiringSoon()) {
        logger.warn("[FaceVerification] Token expiring soon, aborting consent definition fetch");
        handleVerificationError("token_expired", "Session token has expired");
        return;
      }

      try {
        await fetchConsentDefinition(sessionToken);
        logger.log("[FaceVerification] Consent definition fetched");
      } catch (err) {
        logger.error("[FaceVerification] Failed to fetch consent definition:", err);
        const httpStatus = (err as any).httpStatus as number | undefined;
        const apiMessage = (err as any).apiMessage ?? null;

        if (httpStatus === 401) {
          handleVerificationError("token_expired", "Session token has expired");
          return;
        }

        const message = apiMessage ?? 'Consent Get API failed';
        trackAPIError(err, 'GET /v1/consents/definitions', {
          flow: 'face_liveness',
          verificationId: verificationId ?? undefined,
          sessionTokenPresent: !!sessionToken,
          httpStatus,
          errorData: { apiMessage },
        });
        handleVerificationError("consent_get_api_failed", message, "consent_get_failed");
      }
    };

    fetchDefinition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionToken, consentEnabled]);

  // Handle consent acceptance when checkbox is checked
  const handleAcceptConsent = async () => {
    if (!sessionToken || !definitionUuid) {
      logger.error("[FaceVerification] Missing sessionToken or definitionUuid");
      throw new Error('Missing sessionToken or definitionUuid');
    }

    logger.log("[FaceVerification] Calling POST consent API...");
    logger.log("[FaceVerification] definitionUuid:", definitionUuid);

    // Call the real consent API
    const response = await fetch(`${getConsentApiBaseUrl()}/v1/consents`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionToken}`,
      },
      body: JSON.stringify({ definitionUuid })
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => null);
      const apiMessage = errorBody?.errors?.[0]?.message || errorBody?.detail || null;
      const err = new Error(apiMessage ?? `Consent API failed: ${response.status}`);
      (err as any).apiMessage = apiMessage;
      (err as any).httpStatus = response.status;
      throw err;
    }

    const data = await response.json();
    logger.log("[FaceVerification] Consent recorded successfully:", data);
    return data;
  };

  // Handle consent GET failure (both page-load fetch and modal policy fetch).
  // 401 strongly indicates token expiry — tell IVA to retry.
  // Any other error also kills the session: without a valid definition UUID the POST will fail too.
  const handleConsentGetFailed = (err: Error) => {
    const httpStatus = (err as any).httpStatus as number | undefined;
    if (httpStatus === 401 || isSessionTokenExpiringSoon()) {
      handleVerificationError("token_expired", "Session token has expired");
    } else {
      const apiMessage = (err as any).apiMessage ?? null;
      trackAPIError(err, 'GET /v1/consents/definitions', {
        flow: 'face_liveness',
        verificationId: verificationId ?? undefined,
        sessionTokenPresent: !!sessionToken,
        httpStatus,
        errorData: { apiMessage },
      });
      handleVerificationError("consent_get_api_failed", apiMessage ?? 'Consent Get API failed', "consent_get_failed");
    }
  };

  // Handle consent failure: cancel iProov and the transaction.
  // Sets consentFailureRef so the iProov error_transaction_canceled event
  // uses consent error codes instead of generic iProov codes.
  const handleConsentFailed = (error: Error) => {
    const isTimeout = error.message === 'Consent request timed out';
    const message = isTimeout
      ? 'Consent API timed out'
      : (error as any).apiMessage ?? 'Consent API request failed';

    logger.error("[FaceVerification] Consent failed, canceling iProov:", error.message);

    trackAPIError(error, 'POST /v1/consents', {
      flow: 'face_liveness',
      verificationId: verificationId ?? undefined,
      sessionTokenPresent: !!sessionToken,
      httpStatus: (error as any).httpStatus ?? undefined,
      errorData: {
        isTimeout,
        apiMessage: (error as any).apiMessage ?? undefined,
        userMessage: message ?? undefined,
      },
    });

    consentFailureRef.current = { isTimeout, message };

    // Eager cancel: abort the transaction now rather than waiting for the state change
    // that unmounts the element. Flagged so the teardown effect does not repeat it.
    if (iproovRef.current && !cancelledRef.current) {
      cancelledRef.current = true;
      (iproovRef.current as any).cancelTransaction?.();
    }
  };

  return (
    <div className={styles.pageContainer}>
      <div className={styles.container}>
        <div className={styles.contentContainer}>
          {/* VEX-7983: with the Fragno logo on, BrandLayout renders the shared header instead */}
          {!fragnoLogoEnabled && (
            <div className={styles.logoContainer}>
              <div className={styles.logo}>
                <img src="/assets/svg/idme-logo.svg" alt="IDMe" />
              </div>
            </div>
          )}
          {state === "initializing" && (
            <div className={styles.card}>
              <div className={styles.spinner}></div>
            </div>
          )}

          {/* iProov - renders immediately with consent handling in button slot.
              Kept mounted during "permission_blocked" so iProov's permission_denied
              slot (with its Reload recovery button) stays visible. */}
          {(state === "ready" || state === "capturing" || state === "permission_blocked" || state === "capture_failed") && iproovToken && iproovBaseUrl && (
            <>
              <div className={styles.card}>
                {progress.message && <p className={styles.progress}>{progress.message}</p>}
                <div className={styles.iproovContainer}>
                  <iproov-me
                    key={iproovToken}
                    {...({ ref: iproovRef } as any)}
                    token={iproovToken}
                    base_url={iproovBaseUrl}
                    assets_url={`${window.location.origin}/`}
                    logo={fragnoLogoEnabled ? "/assets/svg/idme-logo-rebrand-dark.svg" : "/assets/svg/idme-logo.svg"}
                    language={getIProovLanguage(i18n.language)}
                    filter="vibrant"
                    network_timeout={60}
                  >
                    <IProovSlots
                      sessionToken={sessionToken}
                      consentEnabled={consentEnabled}
                      onAcceptConsent={consentEnabled ? handleAcceptConsent : undefined}
                      onConsentFailed={consentEnabled ? handleConsentFailed : undefined}
                      isSessionTokenExpiringSoon={isSessionTokenExpiringSoon}
                      onConsentGetFailed={handleConsentGetFailed}
                      onReload={requestHostReload}
            onRetry={retryCapture}
            retryAllowed={retryAllowed}
                      blockedPermission={blockedPermission}
                    />
                  </iproov-me>
                  {showFlashingLightsLink && (
                    <button
                      type="button"
                      className={styles.flashingLightsButton}
                      onClick={() => {
                        trackClicked(DOCUMENT_CAPTURE_EVENTS.CLICKED.OPEN_FLASHING_LIGHTS_NOTICE, "link", { section: DOCUMENT_CAPTURE_SECTIONS.INFO, targetAction: "open" });
                        setIsFlashingLightsOpen(true);
                      }}
                    >
                      {t('face.flashingLightsNotice')}
                    </button>
                  )}
                </div>

              </div>
              <Footer />
              {isFlashingLightsOpen && createPortal(
                <div className={styles.flashingLightsOverlay} onClick={() => setIsFlashingLightsOpen(false)}>
                  <div className={styles.flashingLightsModal} onClick={(e) => e.stopPropagation()}>
                    <div className={styles.flashingLightsHeader}>
                      <h2 className={styles.flashingLightsTitle}>{t('face.flashingLights.title')}</h2>
                      <button
                        type="button"
                        className={styles.flashingLightsClose}
                        onClick={() => setIsFlashingLightsOpen(false)}
                        aria-label="Close"
                      >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M18 6L6 18M6 6L18 18" stroke="#272727" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    </div>
                    <p className={styles.flashingLightsBody}>{t('face.flashingLights.body')}</p>
                  </div>
                </div>,
                document.body
              )}
            </>
          )}

          {state === "processing" && (
            <div className={styles.card}>
              <h2 className={styles.title}>{t('face.processing.title')}</h2>
              <div className={styles.spinner}></div>
            </div>
          )}

          {state === "completed" && (
            <div className={styles.card}>
              <div className={styles.successIcon}>✓</div>
              <h2 className={styles.title}>{t('face.completed.title')}</h2>
              <p className={styles.subtitle}>{t('face.completed.subtitle')}</p>
            </div>
          )}

          {state === "error" && (
            <div className={styles.card}>
              <div className={styles.errorIcon}>✕</div>
              <h2 className={styles.title}>{t('face.verificationFailed.title')}</h2>
              <p className={styles.error}>{error}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
